import { useState, useEffect, useRef } from "react";

export enum TypingMode {
    Word = "word",
    Character = "character",
}

/**
 * A hook that progressively reveals text word by word.
 *
 * @returns The accumulated text that has been revealed so far
 */
export function useProgressiveText(
    text: string,
    options: {
        /** Speed in milliseconds per word */
        speed?: number;
        /** The mode to use for typing */
        mode?: TypingMode;
    } = {},
) {
    const { speed = 30 } = options;
    const [displayedText, setDisplayedText] = useState("");
    const currentIndex = useRef(0);
    const accumulatedText = useRef("");
    const mostRecentlyAdded = useRef("");

    useEffect(() => {
        if (!text) {
            setDisplayedText(text || "");
            return;
        }

        const words = (options.mode ?? TypingMode.Word) === TypingMode.Word ? text.split(/(\s+)/) : text.split("");

        const intervalId = setInterval(() => {
            if (currentIndex.current >= words.length) {
                clearInterval(intervalId);
                return;
            }

            // If the text is updated, and the updated text extends the previous word we added, then we need to handle it
            // e.g. t1: "I want cof", t2: "I want coffee please" then we should complete the word "coffee" rather
            // than move on to "please"
            const previousWord = words[currentIndex.current - 1];
            if (previousWord && mostRecentlyAdded.current !== previousWord) {
                const existingPart = mostRecentlyAdded.current;
                const newPart = previousWord.slice(existingPart.length);
                const fullWord = existingPart + newPart;

                mostRecentlyAdded.current = fullWord;
                accumulatedText.current += newPart;
                setDisplayedText(accumulatedText.current);

                // Early return, so we don't increment the index yet
                return;
            }

            const nextWord = words[currentIndex.current];
            accumulatedText.current += nextWord;
            setDisplayedText(accumulatedText.current);
            mostRecentlyAdded.current = nextWord;
            currentIndex.current++;
        }, speed);

        return () => {
            clearInterval(intervalId);
        };
    }, [text, speed, options.mode]);

    return {
        text: displayedText,
    };
}
